<template>
  <div class="container v-content-container">
    <div class="row">
      <div class="col-xs-12">
        <h1>
          <i class="fa fa-fw fa-lg fa-dot-circle-o"></i
          >{{
            `${$label("facility.facility", "Facility")}${
              noDataExist ? "" : ": " + facilityName
            }`
          }}
          <select
            v-model="selectedFacilityId"
            class="form-control course-select pull-right"
            style="margin-right: 15px;"
          >
            <option
              v-for="facility in facilities.data"
              :value="facility.id"
              v-bind:key="facility.id"
              >{{ facility.name }}</option
            >
          </select>
        </h1>
      </div>
    </div>

    <div class="row"><div class="col-xs-12">&nbsp;</div></div>

    <error404 v-if="noDataExist" />

    <template v-else>
      <div class="row">
        <div class="col-xs-12 text-center">
          <div class="alert alert-info">
            <button
              type="button"
              class="close"
              data-dismiss="alert"
              aria-hidden="true"
            >
              &times;
            </button>
            <i class="fa fa-fw fa-lg fa-info-circle"></i> Gültig für alle Venues
            an diesem Standort. Anpassungen können in den
            <a href="/settings/events/venues" class="alert-link"
              >Venue Einstellungen</a
            >
            vorgenommen werden.
          </div>
        </div>
      </div>

      <div v-show="loading" class="row">
        <div class="col-xs-12">
          <view-loader
            margin-top-pixels="15"
            border-color="primary"
          ></view-loader>
        </div>
      </div>

      <alert-box
        v-show="!loading && selectedFacility === null"
        type="warning"
        id="alert-no-venue"
        :closeable="false"
        :show-icon="false"
      >
        {{ $label("user.no_facility_exist", "No facility exist.") }}
      </alert-box>

      <div v-show="!loading" class="row">
        <div class="col-sm-6" v-if="selectedFacility !== null">
          <h2>
            <i class="fa fa-fw fa-lg fa-map-marker"></i>
            {{ $label("facility.address", "Address") }}
          </h2>
          <p></p>
          <div class="v-form-partial">
            <div class="v-form">
              <fieldset>
                <div class="row">
                  <!--BEGIN Facility name-->
                  <section class="col col-8">
                    <text-input
                      v-model="selectedFacility.name"
                      id="facility-name"
                      :label="$label('facility.name', 'Name')"
                      :placeholder="$label('facility.name', 'Name')"
                      :has-error="hasError('name')"
                      :error="getError('name')"
                      :is-disabled="updating"
                      @keyup.native.enter="updateFacility"
                    >
                    </text-input>
                  </section>
                  <!--END Facility name-->
                  <!--BEGIN Facility short name-->
                  <section class="col col-4">
                    <text-input
                      v-model="selectedFacility.short_name"
                      id="facility-short-name"
                      :label="$label('facility.short_name', 'Short name')"
                      :placeholder="$label('facility.short_name', 'Short name')"
                      :has-error="hasError('short_name')"
                      :error="getError('short_name')"
                      :is-disabled="updating"
                      @keyup.native.enter="updateFacility"
                    >
                    </text-input>
                  </section>
                  <!--END Facility short name-->
                </div>

                <div class="row">
                  <!-- BEGIN Country -->
                  <section class="col col-6">
                    <label class="label">{{ $label("common.country") }}</label>

                    <label class="select">
                      <select
                        id="facility-address-country"
                        v-model="selectedFacility.address.country_id"
                        :disabled="updating"
                      >
                        <option
                          v-for="country in countries"
                          :value="country.id"
                          :selected="countryShouldBeSelected(country)"
                          v-bind:key="country.id"
                          >{{
                            $label("common.country_" + country.slug)
                          }}</option
                        >
                      </select>
                      <i></i>
                    </label>

                    <p v-if="hasError('country_id')" class="text-danger">
                      {{ getError("country_id") }}
                    </p>
                  </section>
                  <!-- END Country -->
                  <!-- BEGIN State -->
                  <section class="col col-6">
                    <label class="label">{{
                      $label("common.state", "State")
                    }}</label>

                    <label class="select">
                      <select
                        id="facility-address-state"
                        v-model="selectedFacility.address.state_id"
                        :disabled="updating"
                      >
                        <option
                          v-for="state in statesOfSelectedCountry"
                          :value="state.id"
                          :selected="stateShouldBeSelected(state)"
                          v-bind:key="state.id"
                          >{{ state.name }}</option
                        >
                      </select>
                      <i></i>
                    </label>

                    <p v-if="hasError('state_id')" class="text-danger">
                      {{ getError("state_id") }}
                    </p>
                  </section>
                  <!-- END State -->
                </div>

                <div class="row">
                  <!-- BEGIN City -->
                  <section class="col col-8">
                    <text-input
                      v-model="selectedFacility.address.city"
                      id="facility-address-city"
                      :label="$label('common.city', 'City')"
                      :placeholder="$label('common.city')"
                      :has-error="hasError('city')"
                      :error="getError('city')"
                      :is-disabled="updating"
                      @keyup.native.enter="updateFacility"
                    />
                  </section>
                  <!-- END City -->
                  <!-- BEGIN Post code -->
                  <section class="col col-4">
                    <text-input
                      v-model="selectedFacility.address.post_code"
                      id="facility-address-post-code"
                      :label="$label('common.post_code_short', 'Post code')"
                      :placeholder="$label('common.post_code_short')"
                      :has-error="hasError('post_code')"
                      :error="getError('post_code')"
                      :is-disabled="updating"
                      @keyup.native.enter="updateFacility"
                    />
                  </section>
                  <!-- END Post code -->
                </div>

                <div class="row">
                  <!-- BEGIN Street name -->
                  <section class="col col-8">
                    <text-input
                      v-model="selectedFacility.address.street_name"
                      id="facility-address-street-name"
                      :label="$label('common.street_name_short', 'Street name')"
                      :placeholder="$label('common.street_name_long')"
                      :has-error="hasError('street_name')"
                      :error="getError('street_name')"
                      :is-disabled="updating"
                      @keyup.native.enter="updateFacility"
                    />
                  </section>
                  <!-- END Street name -->
                  <!-- BEGIN Street number -->
                  <section class="col col-4">
                    <text-input
                      v-model="selectedFacility.address.street_number"
                      id="facility-address-street-number"
                      :label="$label('common.street_number', 'Street number')"
                      :placeholder="$label('common.street_number')"
                      :has-error="hasError('street_number')"
                      :error="getError('street_number')"
                      :is-disabled="updating"
                      @keyup.native.enter="updateFacility"
                    />
                  </section>
                  <!-- END Street number -->
                </div>

                <div class="row">
                  <!-- BEGIN additional information -->
                  <section class="col col-12">
                    <label class="label">{{
                      $label("common.additional_address_info_short")
                    }}</label>
                    <label class="textarea">
                      <textarea
                        v-model="
                          selectedFacility.address.additional_information
                        "
                        id="facility-address-additional-information"
                        :class="{
                          'text-input-error': hasError('additional_information')
                        }"
                        :placeholder="
                          $label('common.additional_address_info_long')
                        "
                        :disabled="updating"
                        rows="5"
                      >
                      </textarea>
                    </label>
                    <p
                      v-if="hasError('additional_information')"
                      class="text-danger"
                    >
                      {{ getError("additional_information") }}
                    </p>
                  </section>
                  <!-- END Additional information -->
                </div>
              </fieldset>
            </div>
          </div>
        </div>
        <div class="col-sm-6" v-if="selectedFacility !== null">
          <h2>
            <i class="fa fa-fw fa-lg fa-clock-o"></i>
            {{ $label("facility.opening_times", "Opening times") }}
          </h2>
          <p></p>
          <div class="v-form-partial">
            <div class="v-form">
              <fieldset>
                <template v-if="!isDiffDays">
                  <div class="row">
                    <section class="col col-12">
                      <label
                        v-for="weekday in weekdays"
                        :key="weekday.id"
                        class="btn btn-lg btn-default v-form-button"
                        :id="'facility-day-' + weekday.name"
                        :class="{
                          'checked-weekday': weekday.checked,
                          'is-disabled': updating
                        }"
                        @change="toggleCheckbox('weekday', weekday.id)"
                        >{{ weekday.abbr }}
                        <input type="checkbox" class="display-none" />
                      </label>
                    </section>
                  </div>
                  <div class="row">
                    <!-- BEGIN Start time -->
                    <section class="col col-6">
                      <label class="label">{{
                        $label("facility.starts_at", "Starts at")
                      }}</label>
                      <date-picker
                        v-model="starts_at"
                        :format="timeFormat"
                        :readonly="updating"
                        :disabled="updating"
                        :placeholder="timeFormat"
                        valueType="format"
                        type="time"
                        class="input"
                        id="facility-starts-at"
                      >
                      </date-picker>
                    </section>
                    <!-- END Start time -->

                    <!-- BEGIN End time -->
                    <section class="col col-6">
                      <label class="label">{{
                        $label("facility.ends_at", "Ends at")
                      }}</label>
                      <date-picker
                        v-model="ends_at"
                        :format="timeFormat"
                        :readonly="updating"
                        :disabled="updating"
                        :placeholder="timeFormat"
                        valueType="format"
                        type="time"
                        class="input"
                        id="facility-ends-at"
                      >
                      </date-picker>
                    </section>
                    <!-- END End time -->
                  </div>
                </template>
                <div class="row">
                  <section class="col col-12">
                    <label class="checkbox">
                      <input
                        type="checkbox"
                        id="facility-is-same-days"
                        v-model="isDiffDays"
                      />
                      Tage mit abweichenden Zeiten
                      <i></i>
                    </label>
                  </section>
                </div>

                <template v-if="isDiffDays">
                  <div
                    class="row"
                    v-for="weekday in weekdays"
                    v-bind:key="weekday.id"
                  >
                    <section class="col col-2">
                      <label class="label">{{ space }}</label>
                      <label
                        class="btn btn-lg btn-default v-form-button"
                        :id="'facility-day-' + weekday.name"
                        :class="{
                          'checked-weekday': weekday.checked
                        }"
                        @change="toggleCheckbox('weekday', weekday.id)"
                        >{{ weekday.abbr }}
                        <input type="checkbox" class="display-none" />
                      </label>
                    </section>
                    <!-- BEGIN Start time -->
                    <section class="col col-5">
                      <label class="label">{{
                        $label("facility.starts_at", "Starts at")
                      }}</label>
                      <date-picker
                        v-model="weekday.starts_at"
                        :format="timeFormat"
                        :readonly="updating"
                        :disabled="updating"
                        :placeholder="timeFormat"
                        valueType="format"
                        type="time"
                        class="input"
                        :id="'facility-starts-at-' + weekday.name"
                        :class="{
                          'text-input-error': hasError(
                            'open_times.' + weekday.name + '.starts_at'
                          )
                        }"
                      >
                      </date-picker>

                      <p
                        v-if="
                          hasError('open_times.' + weekday.name + '.starts_at')
                        "
                        class="text-danger"
                      >
                        {{
                          getError("open_times." + weekday.name + ".starts_at")
                        }}
                      </p>
                    </section>
                    <!-- END Start time -->

                    <!-- BEGIN End time -->
                    <section class="col col-5">
                      <label class="label">{{
                        $label("facility.ends_at", "Ends at")
                      }}</label>
                      <date-picker
                        v-model="weekday.ends_at"
                        :format="timeFormat"
                        :readonly="updating"
                        :disabled="updating"
                        :placeholder="timeFormat"
                        valueType="format"
                        type="time"
                        class="input"
                        :id="'facility-ends-at-' + weekday.name"
                        :class="{
                          'text-input-error': hasError(
                            'open_times.' + weekday.name + '.ends_at'
                          )
                        }"
                      >
                      </date-picker>

                      <p
                        v-if="
                          hasError('open_times.' + weekday.name + '.ends_at')
                        "
                        class="text-danger"
                      >
                        {{
                          getError("open_times." + weekday.name + ".ends_at")
                        }}
                      </p>
                    </section>
                    <!-- END End time -->
                  </div>
                </template>
              </fieldset>
            </div>
          </div>
        </div>
      </div>

      <div v-show="!loading" class="row">
        <div class="col-sm-6 tooltip-wrapper" v-if="selectedFacility !== null">
          <h2>
            <i class="fa fa-fw fa-lg fa-user"></i>
            {{ $label("facility.contact_person", "Contact person") }}
          </h2>
          <p></p>
          <div class="v-form-partial">
            <div class="v-form">
              <fieldset>
                <div class="row">
                  <!--BEGIN Title-->
                  <section class="col col-12">
                    <text-input
                      v-model="selectedFacility.contact_person.title"
                      id="facility-contact-person-title"
                      :label="$label('common.title', 'Title')"
                      :placeholder="$label('common.title', 'Title')"
                      :has-error="hasError('contact_person.title')"
                      :error="getError('contact_person.title')"
                      :is-disabled="updating"
                      @keyup.native.enter="updateFacility"
                    />
                  </section>
                  <!--END Title-->
                </div>

                <div class="row">
                  <!-- BEGIN Last name -->
                  <section class="col col-6">
                    <text-input
                      v-model="selectedFacility.contact_person.last_name"
                      id="facility-contact-person-last-name"
                      :label="$label('common.last_name', 'Last name')"
                      :placeholder="$label('common.last_name', 'Last name')"
                      :has-error="hasError('contact_person.last_name')"
                      :error="getError('contact_person.last_name')"
                      :is-disabled="updating"
                      @keyup.native.enter="updateFacility"
                    />
                  </section>
                  <!-- END Last name -->
                  <!-- BEGIN First name -->
                  <section class="col col-6">
                    <text-input
                      v-model="selectedFacility.contact_person.first_name"
                      id="facility-contact-person-first-name"
                      :label="$label('common.first_name', 'First name')"
                      :placeholder="$label('common.first_name')"
                      :has-error="hasError('contact_person.first_name')"
                      :error="getError('contact_person.first_name')"
                      :is-disabled="updating"
                      @keyup.native.enter="updateFacility"
                    />
                  </section>
                  <!-- END First name -->
                </div>

                <div class="row">
                  <!-- BEGIN Email address -->
                  <section class="col col-9">
                    <text-input
                      v-model="selectedFacility.contact_person.email"
                      id="facility-contact-person-email"
                      :label="
                        $label('common.email_address_long', 'E-Mail addres')
                      "
                      :placeholder="$label('common.email_address_short')"
                      :has-error="hasError('contact_person.email')"
                      :error="getError('contact_person.email')"
                      :is-disabled="updating"
                      @keyup.native.enter="updateFacility"
                    />
                  </section>
                  <!-- END Email address -->
                  <!-- BEGIN Gender -->
                  <section class="col col-3">
                    <div class="btn-group">
                      <label class="label">{{ $label("common.gender") }}</label>
                      <label
                        @click="setGender(2)"
                        class="btn btn-default v-form-button"
                        :class="{ active: isFemale, 'is-disabled': updating }"
                      >
                        <i class="fa fa-fw fa-lg fa-female"></i>
                      </label>
                      <label
                        @click="setGender(1)"
                        class="btn btn-default v-form-button"
                        :class="{ active: isMale, 'is-disabled': updating }"
                      >
                        <i class="fa fa-fw fa-lg fa-male"></i>
                      </label>
                    </div>
                  </section>
                  <!-- END Gender -->
                </div>

                <div class="row">
                  <!-- BEGIN Phone country code -->
                  <section class="col col-3">
                    <text-input
                      v-model="
                        selectedFacility.contact_person.phone_number.prefix
                      "
                      id="facility-contact-person-phone-number-prefix"
                      :label="$label('common.prefix', 'Prefix')"
                      placeholder="+43"
                      :has-error="
                        hasError('contact_person.phone_number.prefix')
                      "
                      :error="getError('contact_person.phone_number.prefix')"
                      :is-disabled="updating"
                      @keyup.native.enter="updateFacility"
                    />
                  </section>
                  <!-- END Phone country code -->
                  <!-- BEGIN Phone number -->
                  <section class="col col-9">
                    <text-input
                      v-model="
                        selectedFacility.contact_person.phone_number.number
                      "
                      id="facility-contact-person-phone-number"
                      :label="$label('common.phone_number', 'Phone number')"
                      placeholder="XXXXXXXX"
                      :has-error="
                        hasError('contact_person.phone_number.number')
                      "
                      :error="getError('contact_person.phone_number.number')"
                      :is-disabled="updating"
                      icon="icon-phone"
                      @keyup.native.enter="updateFacility"
                    />
                  </section>
                  <!-- END Phone number -->
                </div>

                <div class="row">
                  <!-- BEGIN Fax country code -->
                  <section class="col col-3">
                    <text-input
                      v-model="
                        selectedFacility.contact_person.fax_number.prefix
                      "
                      id="facility-contact-person-fax-number-prefix"
                      :label="$label('common.prefix', 'Prefix')"
                      placeholder="+43"
                      :has-error="hasError('fax_number.prefix')"
                      :error="getError('fax_number.prefix')"
                      :is-disabled="updating"
                      @keyup.native.enter="updateFacility"
                    />
                  </section>
                  <!-- END Fax country code -->
                  <!-- BEGIN Fax number -->
                  <section class="col col-9">
                    <text-input
                      v-model="
                        selectedFacility.contact_person.fax_number.number
                      "
                      id="facility-contact-person-fax-number"
                      :label="$label('common.fax_number', 'Fax number')"
                      placeholder="XXXXXXXX"
                      :has-error="hasError('fax_number.number')"
                      :error="getError('fax_number.number')"
                      :is-disabled="updating"
                      icon="icon-print"
                      @keyup.native.enter="updateFacility"
                    />
                  </section>
                  <!-- END Fax number -->
                </div>
              </fieldset>
            </div>
          </div>
        </div>
        <div class="col-sm-6" v-if="selectedFacility !== null">
          <h2>
            <i class="fa fa-fw fa-lg fa-thumb-tack"></i>
            {{ $label("facility.services", "Services") }}
          </h2>
          <p></p>
          <div id="facility-services" class="v-form-partial">
            <div class="v-form">
              <fieldset>
                <div
                  class="row"
                  v-for="category in serviceCategories"
                  :key="category.name"
                  v-show="category.services && category.services.length"
                >
                  <section class="col col-12">
                    <label class="label"
                      ><b>{{ category.name }}</b></label
                    >
                    <section
                      class="col col-4 mb-2"
                      v-for="service in category.services"
                      :key="service.id"
                    >
                      <label
                        class="checkbox text-ellipsis"
                        :class="{ 'is-disabled': updating }"
                      >
                        <input
                          type="checkbox"
                          :id="'facility-service-' + service.id"
                          :checked="
                            selectedFacility.service_ids.indexOf(service.id) >
                              -1
                          "
                          @change="toggleCheckbox('service', service.id)"
                        />
                        {{ service.name }}
                        <i></i>
                      </label>
                    </section>
                  </section>
                </div>
              </fieldset>
            </div>
          </div>
        </div>
      </div>

      <div v-show="!loading" class="row">
        <div class="col-xs-12">&nbsp;</div>
      </div>
      <hr v-show="!loading" />

      <div v-show="!loading" class="row" style="margin-bottom: 16px;">
        <div class="col-xs-12 text-center">
          <info-button
            :class="{ 'is-disabled': updating || !selectedFacility }"
            id="save-facility-button"
            icon="fa fa-lg fa-fw fa-save"
            @click.native="updateFacility"
            :text="$label('common.save', 'Save')"
          >
          </info-button>
        </div>
      </div>

      <div v-show="!loading" class="row">
        <div class="col-xs-12">&nbsp;</div>
      </div>
    </template>
  </div>
</template>

<script>
import Api from "@/api.js";
import FormErrorsMixin from "@/mixins/FormErrors.js";
import swal from "sweetalert";

import DatePicker from "vue2-datepicker";
import "vue2-datepicker/index.css";
import "vue2-datepicker/locale/de";

import ViewLoader from "@/components/Common/ViewLoader.vue";
import AlertBox from "@/components/Common/Alerts/AlertBox";
import TextInput from "@/components/Common/Inputs/TextInput.vue";
import InfoButton from "@/components/Common/Buttons/InfoButton";
import Error404 from "@/components/Common/Error404";

export default {
  mixins: [FormErrorsMixin],

  components: {
    ViewLoader,
    AlertBox,
    TextInput,
    DatePicker,
    InfoButton,
    Error404
  },

  data() {
    return {
      facilities: [],
      loading: false,
      space: "\xa0",
      updating: false,
      noDataExist: false,
      selectedFacility: null,
      selectedFacilityId: null,
      countries: [],
      serviceCategories: [],
      starts_at: null,
      ends_at: null,
      timeFormat: "HH:mm",
      isDiffDays: false,
      weekdays: [
        {
          id: 0,
          abbr: "MO",
          name: "monday",
          checked: false,
          starts_at: null,
          ends_at: null
        },
        {
          id: 1,
          abbr: "TU",
          name: "tuesday",
          checked: false,
          starts_at: null,
          ends_at: null
        },
        {
          id: 2,
          abbr: "WE",
          name: "wednesday",
          checked: false,
          starts_at: null,
          ends_at: null
        },
        {
          id: 3,
          abbr: "TH",
          name: "thursday",
          checked: false,
          starts_at: null,
          ends_at: null
        },
        {
          id: 4,
          abbr: "FR",
          name: "friday",
          checked: false,
          starts_at: null,
          ends_at: null
        },
        {
          id: 5,
          abbr: "SA",
          name: "saturday",
          checked: false,
          starts_at: null,
          ends_at: null
        },
        {
          id: 6,
          abbr: "SU",
          name: "sunday",
          checked: false,
          starts_at: null,
          ends_at: null
        }
      ],
      validatedInputs: [
        "name",
        "short_name",
        "address.country_id",
        "address.state_id",
        "address.city",
        "post_code",
        "address.street_name",
        "address.street_number",
        "address.additional_information",
        "contact_person.title",
        "contact_person.first_name",
        "contact_person.last_name",
        "contact_person.email",
        "contact_person.phone_number.prefix",
        "contact_person.phone_number.number",
        "contact_person.fax_number.prefix",
        "contact_person.fax_number.number"
      ]
    };
  },

  beforeRouteEnter(to, from, next) {
    next(vm => {
      vm.selectedFacilityId = vm.$route.params.id;
      vm.getFacilities(() => {
        if (
          vm.selectedFacilityId !== null &&
          vm.selectedFacilityId !== undefined &&
          vm.selectedFacilityId !== "default"
        ) {
          vm.getFacility(() => {
            vm.getCountries(() => {
              vm.getServices();
            });
          });
        }
      });
    });
  },

  beforeRouteUpdate(to, from, next) {
    this.getFacilities(() => {
      if (
        this.selectedFacilityId !== null &&
        this.selectedFacilityId !== undefined &&
        this.selectedFacilityId !== "default"
      ) {
        this.getFacility(() => {
          this.getCountries(() => {
            this.getServices();
          });
        });
      }
    });
    next();
  },

  mounted() {
    //
  },

  methods: {
    toggleCheckbox(type, id) {
      if (type === "weekday") {
        this.weekdays[id].checked = !this.weekdays[id].checked;
        if (this.starts_at) {
          this.weekdays[id].starts_at = this.starts_at;
        }
        if (this.ends_at) {
          this.weekdays[id].ends_at = this.ends_at;
        }
      }
      if (type === "service") {
        const index = this.selectedFacility.service_ids.indexOf(id);
        if (index > -1) {
          this.selectedFacility.service_ids.splice(index, 1);
        } else {
          this.selectedFacility.service_ids.push(id);
        }
      }
    },

    setGender(gender) {
      this.selectedFacility.contact_person.gender = gender;
    },

    getFacilities(successCallback) {
      const endpoint = this.$partnerEndpoint("facilities/simplified");

      Api()
        .get(endpoint)
        .then(response => {
          this.facilities = response.data;
          if (
            !this.selectedFacilityId ||
            this.selectedFacilityId === "default"
          ) {
            this.selectedFacilityId = response.data.data[0].id;
          }
          if (typeof successCallback == "function") {
            successCallback(this.facilities);
          }
        })
        .catch(error => {
          console.log(error);
        });
    },

    getFacility(successCallback) {
      if (this.loading) {
        return;
      }

      this.loading = true;
      const endpoint = this.$partnerEndpoint(
        "facilities/" + this.selectedFacilityId
      );

      Api()
        .get(endpoint)
        .then(response => {
          this.selectedFacility = response.data.data;
          this.selectedFacility.service_ids = response.data.data.services.map(
            service => service.id
          );
          this.weekdays = this.weekdays.map(weekday => ({
            ...weekday,
            checked: false,
            starts_at: null,
            ends_at: null
          }));
          response.data.data.opening_times.map((time, index) => {
            if (index === 0) {
              this.isDiffDays = false;
              this.starts_at = time.open_time;
              this.ends_at = time.close_time;
            }
            this.weekdays[time.day - 1].checked = true;
            this.weekdays[time.day - 1].starts_at = time.open_time;
            this.weekdays[time.day - 1].ends_at = time.close_time;
            if (
              this.starts_at !== time.open_time ||
              this.ends_at !== time.close_time
            ) {
              this.isDiffDays = true;
              this.starts_at = null;
              this.ends_at = null;
            }
          });
          if (typeof successCallback == "function") {
            successCallback();
          }
        })
        .catch(error => {
          if (error.response.status === 404) {
            this.noDataExist = true;
          }
          console.log(error.response);
        })
        .finally(() => {
          this.loading = false;
        });
    },

    updateFacility() {
      if (this.loading || this.updating || !this.selectedFacilityId) {
        return;
      }
      const endpoint = this.$partnerEndpoint(
        "facilities/" + this.selectedFacilityId
      );

      this.resetErrors();
      this.updating = true;
      this.selectedFacility.opening_times = this.weekdays
        .filter(
          weekday => weekday.starts_at && weekday.ends_at && weekday.checked
        )
        .map(weekday => ({
          day: weekday.id + 1,
          times: [
            {
              open_time: weekday.starts_at,
              close_time: weekday.ends_at
            }
          ]
        }));

      Api()
        .put(endpoint, this.selectedFacility)
        .then(() => {
          this.getFacilities(() => {
            this.getFacility(() => {
              swal({
                title: "Success!",
                text: "The facility was updated.",
                icon: "success"
              });
            });
          });
        })
        .catch(error => {
          this.storeErrors(error.response.data.errors);
        })
        .finally(() => {
          this.updating = false;
        });
    },

    getCountries(successCallback) {
      Api()
        .get("api/internal/v1/countries")
        .then(response => {
          this.countries = response.data.data;

          if (typeof successCallback == "function") {
            successCallback();
          }
        })
        .catch(error => {
          console.log(error);
        });
    },

    getServices(successCallback) {
      const endpoint = this.$partnerEndpoint("service-categories");

      Api(endpoint)
        .get(endpoint)
        .then(response => {
          this.serviceCategories = response.data.data;
          if (typeof successCallback == "function") {
            successCallback();
          }
        })
        .catch(error => {
          console.log(error);
        });
    },

    countryShouldBeSelected(country) {
      return this.selectedFacility.address.country_id == country.id;
    },

    stateShouldBeSelected(state) {
      return this.selectedFacility.address.state_id == state.id;
    },

    forceRerender() {
      this.componentKey += 1;
    }
  },

  computed: {
    isFemale() {
      return this.selectedFacility.contact_person.gender == 2;
    },

    isMale() {
      return this.selectedFacility.contact_person.gender == 1;
    },

    facilityName() {
      if (typeof this.facilities.data == "undefined") {
        return "";
      }

      for (var i = 0; i < this.facilities.data.length; i++) {
        if (this.facilities.data[i].id == this.selectedFacilityId) {
          return this.facilities.data[i].name;
        }
      }

      return "";
    },

    statesOfSelectedCountry() {
      for (var i = 0; i < this.countries.length; i++) {
        if (this.countries[i].id != this.selectedFacility.address.country_id) {
          continue;
        }
        return this.countries[i].states;
      }

      return [];
    }
  },

  watch: {
    selectedFacilityId(selectedFacilityId) {
      this.$router.push({
        name: "FacilityGeneralSettings",
        params: { id: selectedFacilityId }
      });
    },
    starts_at(starts_at) {
      if (!this.isDiffDays) {
        this.weekdays.map(weekday => {
          if (weekday.checked) {
            weekday.starts_at = starts_at;
          }
        });
      }
    },
    ends_at(ends_at) {
      if (!this.isDiffDays) {
        this.weekdays.map(weekday => {
          if (weekday.checked) {
            weekday.ends_at = ends_at;
          }
        });
      }
    }
  }
};
</script>

<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>

<style>
#facility-services .mb-2 {
  margin-bottom: 0.6rem !important;
}

.display-none {
  display: none;
}

.checked-weekday {
  color: #333;
  background-color: #e6e6e6;
  border-color: #adadad;
}

.text-ellipsis {
  text-overflow: ellipsis;
  overflow: hidden;
}
</style>
